import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import './AttendanceLog.css';

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

function formatTime(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleTimeString('en-PK', {
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-PK', {
    weekday: 'short', day: 'numeric', month: 'short',
  });
}

function formatDay(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).getDate();
}

function formatWeekday(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-PK', { weekday: 'short' }).toUpperCase();
}

function formatHours(minutes) {
  if (!minutes) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
}

function getDisplayStatus(record) {
  if (record.status === 'weekly_off') return 'weekoff';
  if (record.status === 'public_holiday') return 'holiday';
  if (record.status === 'approved_leave' || record.status === 'casual_auto') return 'leave';
  if (!record.clock_in) return 'absent';
  if (record.late_minutes > 0) return 'late';
  return 'present';
}

export default function AttendanceLog({ employee }) {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth]     = useState(new Date().getMonth() + 1);
  const [year, setYear]       = useState(new Date().getFullYear());

  useEffect(() => { loadData(); }, [month, year]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await api.getMyAttendance(month, year);
      setRecords(data.records || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2024; y--) years.push(y);

  const present     = records.filter(r => r.clock_in && r.late_minutes === 0).length;
  const late        = records.filter(r => r.clock_in && r.late_minutes > 0).length;
  const absent      = records.filter(r => !r.clock_in && r.status !== 'public_holiday' && r.status !== 'approved_leave' && r.status !== 'casual_auto' && r.status !== 'weekly_off').length;
  const holidays    = records.filter(r => r.status === 'public_holiday').length;
  const onLeave     = records.filter(r => r.status === 'approved_leave' || r.status === 'casual_auto').length;
  const weeklyOff   = records.filter(r => r.status === 'weekly_off').length;
  const totalLogged = present + late + absent;
  const presentRate = totalLogged > 0 ? Math.round(((present + late) / totalLogged) * 100) : 0;

  const sorted = [...records].sort((a, b) => new Date(b.date) - new Date(a.date));

  return (
    <div className="log-page">

      <div className="log-period-row">
        <div className="log-period-pills">
          {MONTHS.map((m, i) => (
            <button
              key={i}
              className={`log-month-pill ${month === i + 1 ? 'log-pill-active' : ''}`}
              onClick={() => setMonth(i + 1)}
            >
              {m.slice(0, 3)}
            </button>
          ))}
        </div>
        <div className="log-year-pills">
          {years.map(y => (
            <button
              key={y}
              className={`log-year-pill ${year === y ? 'log-pill-active' : ''}`}
              onClick={() => setYear(y)}
            >
              {y}
            </button>
          ))}
        </div>
      </div>

      <div className="log-hero">
        <div className="log-hero-left">
          <div className="log-hero-pct">{presentRate}%</div>
          <div className="log-hero-pct-lbl">attendance this month</div>
        </div>
        <div className="log-hero-ring-wrap">
          <svg width="64" height="64" viewBox="0 0 64 64">
            <circle cx="32" cy="32" r="26" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="6" />
            <circle cx="32" cy="32" r="26" fill="none"
              stroke="#fff" strokeWidth="6"
              strokeDasharray={2 * Math.PI * 26}
              strokeDashoffset={2 * Math.PI * 26 * (1 - presentRate / 100)}
              strokeLinecap="round"
              transform="rotate(-90 32 32)"
              style={{ transition: 'stroke-dashoffset 1s ease' }}
            />
          </svg>
        </div>
      </div>

      <div className="log-stats">
        <div className="log-stat-v2 log-stat-green">
          <span className="log-stat-v2-icon">✓</span>
          <div className="log-stat-v2-val">{present}</div>
          <div className="log-stat-v2-lbl">Present</div>
        </div>
        <div className="log-stat-v2 log-stat-amber">
          <span className="log-stat-v2-icon">⏱</span>
          <div className="log-stat-v2-val">{late}</div>
          <div className="log-stat-v2-lbl">Late</div>
        </div>
        <div className="log-stat-v2 log-stat-red">
          <span className="log-stat-v2-icon">✗</span>
          <div className="log-stat-v2-val">{absent}</div>
          <div className="log-stat-v2-lbl">Absent</div>
        </div>
        {holidays > 0 && (
          <div className="log-stat-v2 log-stat-purple">
            <span className="log-stat-v2-icon">🎉</span>
            <div className="log-stat-v2-val">{holidays}</div>
            <div className="log-stat-v2-lbl">Holidays</div>
          </div>
        )}
      </div>

      <div className="log-section-label">
        Full Log — {MONTHS[month - 1]} {year}
      </div>

      {loading ? (
        <div className="log-loading">
          <div className="log-spinner" />
        </div>
      ) : sorted.length === 0 ? (
        <div className="log-card">
          <div className="log-empty">
            <div className="log-empty-icon">📋</div>
            No records for {MONTHS[month - 1]} {year}
          </div>
        </div>
      ) : (
        <div className="log-timeline">
          {sorted.map((r, i) => {
            const status  = getDisplayStatus(r);
            const isLate  = r.clock_in && r.late_minutes > 0;
            const ongoing = r.clock_in && !r.clock_out;

            const colorMap = { present: '#10b981', late: '#f59e0b', absent: '#ef4444', holiday: '#8b5cf6', leave: '#3b82f6', weekoff: '#64748b' };
            const iconMap  = { present: '✓', late: '⏱', absent: '✗', holiday: '🎉', leave: '📋', weekoff: '🌙' };
            const badgeMap = {
              present: { l: 'Present',        c: 'bpresent' },
              late:    { l: 'Late',            c: 'blate' },
              absent:  { l: 'Absent',          c: 'babsent' },
              holiday: { l: 'Public Holiday',  c: 'bholiday' },
              leave:   { l: 'On Leave',        c: 'bleave' },
              weekoff: { l: 'Weekly Off',       c: 'bweekoff' },
            };

            const color = colorMap[status];
            const icon  = iconMap[status];
            const badge = badgeMap[status];

            return (
              <div key={i} className="log-tl-row">
                <div className="log-tl-date-col">
                  <div className="log-tl-day">{formatDay(r.date)}</div>
                  <div className="log-tl-weekday">{formatWeekday(r.date)}</div>
                  <div className="log-tl-dot" style={{ background: color, boxShadow: `0 0 0 4px ${color}22` }} />
                  {i < sorted.length - 1 && <div className="log-tl-line" />}
                </div>

                <div className="log-tl-card" style={{ borderLeftColor: color }}>
                  <div className="log-tl-card-top">
                    <div className="log-tl-card-title">
                      <span className="log-tl-icon" style={{ background: `${color}1a`, color }}>{icon}</span>
                      <span className="log-tl-full-date">{formatDate(r.date)}</span>
                    </div>
                    <span className={`badge ${badge.c}`}>{badge.l}</span>
                  </div>

                  <div className="log-tl-times">
                    <div className="log-tl-time-item">
                      <span className="log-tl-time-lbl">IN</span>
                      <span className="log-tl-time-val">{formatTime(r.clock_in)}</span>
                    </div>
                    <div className="log-tl-time-divider">→</div>
                    <div className="log-tl-time-item">
                      <span className="log-tl-time-lbl">OUT</span>
                      <span className="log-tl-time-val">{ongoing ? 'Ongoing' : formatTime(r.clock_out)}</span>
                    </div>
                    <div className="log-tl-hours">
                      {ongoing ? <span className="log-tl-ongoing-tag">● Live</span> : formatHours(r.total_minutes)}
                    </div>
                  </div>

                  {isLate && (
                    <div className="log-tl-late-banner">
                      <span className="late-tag">+{r.late_minutes}m late</span>
                      {r.late_minutes >= 180 ? ' · Full day deduction risk' :
                       r.late_minutes >= 60  ? ' · Half day risk' : ''}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}